'use client';

import type { TraceFile } from '@terra/shared-schemas-ai';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { Collapsible } from '../../../../components/collapsible';
import { MetaDefault } from '../../../../components/typography';
import {
  OobEvidenceLabel,
  OobInteractionsCard,
} from '../../../artifacts/oob/OobInteractionsCard';
import type { OobEvidencePayload } from '../../../artifacts/oob/oob-evidence-payload';
import { useTraceFilesOptional } from '../TraceFilesProvider';
import { type TraceToolCall } from '../types';
import { shouldShowTraceToolShimmer } from '../utils/trace-text-shimmer';
import { resolveOobDetectionPayload } from './oob-detection-payload';

const EMPTY_TRACE_FILES: TraceFile[] = [];

type OobDetectionToolCardProps = {
  toolCall: TraceToolCall;
  displayName: string;
};

const readOutputMessage = (value: unknown): string | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  const message = (value as { message?: unknown }).message;

  return typeof message === 'string' ? message : null;
};

function extractOutputMessage(output: string | undefined): string | null {
  if (!output) {
    return null;
  }

  const resultPrefix = 'Result: ';
  const timingPrefix = '\nTiming: ';
  const resultStart = output.indexOf(resultPrefix);

  if (resultStart === -1) {
    return output.trim() || null;
  }

  const resultEnd = output.indexOf(timingPrefix, resultStart);
  const resultContent =
    resultEnd === -1
      ? output.slice(resultStart + resultPrefix.length)
      : output.slice(resultStart + resultPrefix.length, resultEnd);

  try {
    return readOutputMessage(JSON.parse(resultContent));
  } catch {
    return resultContent.trim() || null;
  }
}

function getModePreview(mode: unknown): string | null {
  switch (mode) {
    case '1':
      return 'Generate';
    case '2':
      return 'Listen';
    case '3':
      return 'Dynamic Response';
    default:
      return null;
  }
}

function OobDetectionResult({
  displayName,
  payload,
}: {
  displayName: string;
  payload: OobEvidencePayload;
}) {
  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="bg.subtle"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="bg.subtle"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <MetaDefault color="fg.default" fontWeight="medium" truncate minW={0}>
          {displayName}
        </MetaDefault>
        <OobEvidenceLabel payload={payload} />
      </HStack>
      <Box px={3} py={2} bg="bg.subtle">
        <OobInteractionsCard payload={payload} />
      </Box>
    </Box>
  );
}

export function OobDetectionToolCard({
  toolCall,
  displayName,
}: OobDetectionToolCardProps) {
  const t = useTranslations('designSystem.chat');
  const traceFiles = useTraceFilesOptional();
  const [isOpen, setIsOpen] = useState(false);
  const files = traceFiles?.traceFiles ?? EMPTY_TRACE_FILES;
  const payload = useMemo(
    () =>
      resolveOobDetectionPayload({
        toolCall,
        files,
      }),
    [toolCall, files],
  );

  if (payload) {
    return <OobDetectionResult displayName={displayName} payload={payload} />;
  }

  const hasError = Boolean(toolCall.error);
  const message = extractOutputMessage(toolCall.output);
  const hasResult = Boolean(message) || hasError;
  const modePreview = getModePreview(toolCall.args?.mode);
  const StatusIcon = () => {
    if (hasError) {
      return (
        <Box color="fg.error" display="flex" alignItems="center">
          <Cancel01Icon size={10} />
        </Box>
      );
    }

    return null;
  };

  const showShimmer = shouldShowTraceToolShimmer({
    hasResult,
    status: toolCall.status,
  });
  const shimmerStyle = showShimmer
    ? {
        background:
          'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animationTimingFunction: 'linear',
      }
    : undefined;
  const toolNameText = (
    <MetaDefault
      color="fg.muted"
      animationStyle={showShimmer ? 'shimmer' : undefined}
      style={shimmerStyle}
    >
      {displayName}
    </MetaDefault>
  );

  if (!hasResult) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        {toolNameText}
        {modePreview ? (
          <MetaDefault
            color="fg.subtle"
            animationStyle={showShimmer ? 'shimmer' : undefined}
            style={shimmerStyle}
          >
            {modePreview}
          </MetaDefault>
        ) : null}
        <Box flex={1} />
        <Box display="flex" alignItems="center">
          <StatusIcon />
        </Box>
      </HStack>
    );
  }

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      lazyMount
      unmountOnExit
    >
      <Collapsible.Trigger asChild>
        <HStack
          w="full"
          gap={1.5}
          alignItems="center"
          py={1}
          cursor="pointer"
          borderRadius="sm"
        >
          {toolNameText}

          {modePreview ? (
            <>
              <MetaDefault color="fg.muted">•</MetaDefault>
              <MetaDefault color="fg.subtle">{modePreview}</MetaDefault>
            </>
          ) : null}

          <Box display="flex" alignItems="center" color="fg.muted">
            {isOpen ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowRight01Icon size={12} />
            )}
          </Box>

          <Box flex={1} />

          <Box display="flex" alignItems="center">
            <StatusIcon />
          </Box>
        </HStack>
      </Collapsible.Trigger>

      <Collapsible.Content>
        <VStack alignItems="stretch" gap={2} width="100%" py={2}>
          {toolCall.error ? (
            <Box
              width="100%"
              borderLeftWidth="2px"
              borderLeftColor="border.error"
              pl={2}
              py={2}
            >
              <MetaDefault color="fg.error">{toolCall.error}</MetaDefault>
            </Box>
          ) : message ? (
            <MetaDefault color="fg.default">{message}</MetaDefault>
          ) : (
            <MetaDefault color="fg.muted" fontStyle="italic">
              {t('noOutput')}
            </MetaDefault>
          )}
        </VStack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
